import {appConfig, gaConfig} from '@stencil/hotels-config';
import {AnyAction} from 'redux';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {AppState} from '../../../verticals/hotels-dweb/src/shared/store';
import {pushEcommerceGAEvent, pushga} from 'ga-utils';
import roomStringUtils from '@stencil/hotels-utils/src/room-string-utils';
import {gaCheckinDateDifference} from '@stencil/hotels-utils/src/date-utils/date-utils';
import parseDateIntoHermesString from '@stencil/hotels-utils/src/date-utils/parse-date-into-hermes-string';
import gaFilterListShortName from '@stencil/hotels-utils/src/get-filter-list-short-name';
import dateFormat from 'dateformat';

export const gaPageViewThunk = (
  pageName,
  additionalData = {},
  cb = () => {},
): ThunkAction<void, AppState, {}, AnyAction> => {
  return (_: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {common, user, srp, details, review} = getState();
    const {destination, checkin, checkout, roomString, partner, country, metaPartner} = common;
    const {srpConfig} = srp;
    const {filter: srpFilter = {}} = srpConfig;
    const {userInfo} = user;

    const dateDelta = gaCheckinDateDifference(checkin.date);

    let cdProfileSelected = 0;
    if (userInfo && userInfo.profile_type) {
      const profile_type = userInfo.profile_type;
      cdProfileSelected = profile_type === 'business' ? 1 : 0;
    }

    const destinationType = (destination && destination.t) || '';
    const cityName = destination?.ct?.n || destination?.n || '';
    const cityId = destination?.ct?._id || destination?._id || '';

    const checkinDateInHermesString =
      (checkin && checkin.date && parseDateIntoHermesString(checkin.date)) || '';
    const checkoutDateInHermesString =
      (checkout && checkout.date && parseDateIntoHermesString(checkout.date)) || '';

    let commonData = {
      event: 'virtualPageView',
      cdCatQuery: partner,
      cdHotelSearchCheckIn: checkinDateInHermesString,
      cdHotelSearchCheckOut: checkoutDateInHermesString,
      cdSubCatQuery:
        (country === appConfig.countryTextMap.india.key && appConfig.countryTextMap.india.sec) ||
        (country && appConfig.countryTextMap.international.sec) ||
        '',
      cdHotelSearchRooms: roomString,

      userEmail: (userInfo && userInfo.email) || '',
      userid: (userInfo && userInfo.userid) || '',

      isMulti: true,
      cdProfileSelected: cdProfileSelected,
      cdMidNightCheckin: false,

      dpCheckIn: checkinDateInHermesString,
      dpCheckOut: checkoutDateInHermesString,
      dpRoomString: roomString,
      dpTimeStamp: parseDateIntoHermesString(new Date()),
      dpFilterList: gaFilterListShortName(srpConfig),
    };

    let data = {};

    let apiLoadTime;

    switch (pageName) {
      case 'srp': {
        let firstFilterLocation =
          Array.isArray(srpFilter?.location_search) &&
          srpFilter?.location_search.length &&
          srpFilter?.location_search[0];

        data = {
          virtualPage: window && window.location && window.location.pathname,
          cdCityName: cityName,
          cdCityVoy: cityId,
          cdLocName: firstFilterLocation?.n || '',
          cdLocVoy: firstFilterLocation?.loc_id || '',
          cdDaysToBooking: dateDelta,
          cdHotelSearchKeyword: destinationType,
          cdHotelSearchTitle: destinationType,
        };
        apiLoadTime = cb();
        if (apiLoadTime) data = {...data, apiLoadTime};
        break;
      }
      case 'details':
        const {staticData, hermesData} = details;
        const rooms = hermesData?.data?.reg || [];
        const firstRoom = rooms[0];
        const {ibp, gtv} = firstRoom?.rpl.length ? firstRoom?.rpl[0] : {ibp: '', gtv: ''};

        data = {
          virtualPage: window && window.location && window.location.pathname,
          cdCityName: staticData?.c,
          cdCityVoy: staticData?.vcid,
          cdLocName: staticData?.l,
          cdLocVoy: '999999',
          cdDaysToBooking: dateDelta,
          cdHotelSearchKeyword: destinationType,
          cdHotelSearchTitle: destinationType,
          cdHotelVoy: staticData?.hc,

          propertyType: staticData?.ht && staticData?.ht.join(),
          cdTraffic: metaPartner ? metaPartner : 'Direct',
          product: staticData?.ty === 'regular' ? 'Hotels' : 'goStays',
          cdHTLReviewsrc: staticData?.gri,
          Vendor: gtv ? `${ibp}_${gtv}` : ibp,
        };
        apiLoadTime = cb();
        if (apiLoadTime) data = {...data, apiLoadTime};

        break;

      case 'review':
        const {descblockResponse = {}, descblockInput} = review;
        const {cdata = {}} = descblockResponse;
        const {gtv: gtvDesc = '', v = '', cityCode, hotelArea} = descblockInput;
        const {vcid = '', city = ''} = cdata;

        data = {
          hotelName: destination.n,
          cdCityName: staticData?.n || city || hotelArea,
          cdCityVoy: staticData?.vcid || vcid || cityCode,
          cdLocName: staticData?.n || city || hotelArea,
          cdLocVoy: staticData?.vcid || vcid || cityCode,
          virtualPage: '/hotels/hotel-booking/',
          Vendor: gtvDesc ? `${v}_${gtvDesc}` : v,
        };
        apiLoadTime = cb();
        if (apiLoadTime) data = {...data, apiLoadTime};

        break;
      default:
        break;
    }
    pushga({event: gaConfig.HotelsDwebGenericEvent, eventCategory: 'newFunnel'});

    pushga({...commonData, ...data, ...additionalData});
  };
};

export const gaPageViewMwebThunk = (
  pageName,
  additionalData = {},
  cb = () => {},
): ThunkAction<void, AppState, {}, AnyAction> => {
  return (_: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {common, user, srp, details, review} = getState();
    const {staticData = {}} = details;
    const {
      destination,
      checkin,
      checkout,
      roomString,
      partner,
      country,
      metaPartner,
      selectedHotel,
    } = common;
    const {srpConfig, isSEO: isSeoSrp} = srp;
    const {userInfo} = user;
    const {filter: srpFilter = {}} = srpConfig;

    const dateDelta = gaCheckinDateDifference(checkin.date);
    const {mwebPageViewGAConfig} = gaConfig;
    let cdProfileSelected = 0;
    if (userInfo && userInfo.profile_type) {
      const profile_type = userInfo.profile_type;
      cdProfileSelected = profile_type === 'business' ? 1 : 0;
    }

    const destinationType = (destination && destination.t) || '';
    const cityName = destination?.ct?.n || destination?.n || '';
    const cityId = destination?.ct?._id || destination?._id || '';
    const checkinDateInHermesString =
      (checkin && checkin.date && parseDateIntoHermesString(checkin.date)) || '';
    const checkoutDateInHermesString =
      (checkout && checkout.date && parseDateIntoHermesString(checkout.date)) || '';

    let commonDataAcrossAllFunnels = {
      cdCatQuery: partner,
      cdSubCatQuery:
        (country === appConfig.countryTextMap.india.key && appConfig.countryTextMap.india.sec) ||
        (country && appConfig.countryTextMap.international.sec) ||
        '',
      userEmail: (userInfo && userInfo.email) || '',
      userid: (userInfo && userInfo.userid) || '',
      cdCityName: cityName,
    };

    let commonDataForMainFunnel = {
      // This is not required for seo-srp
      event: mwebPageViewGAConfig.mwebPageViewCommonEvent,
      cdCodePushversion: '',
      cdExtensionOfTrip: false,
      cdMidNight: checkin.isMidnight,
      cdExpLoc: false,
      VoyagerHotelCityID: cityId,
      cdHotelPaxSelection: roomString,
      cdaltacc: false, //for alt acco flow
      cdHotelSearchKeyword: destinationType,
      cdTravelStyle: (srpFilter?.hctx && `segment || ${srpFilter.hctx}`) || '',
      // propertyType: ht.toString() || cdata.ht || '',
      // cdCaptivate_BookNow: captivateBookNow, // for details page image gallery flow tracking
      // cdIsHappified: happified, for AB of Review and Room Selection
      dpCheckIn: checkinDateInHermesString,
      dpCheckOut: checkoutDateInHermesString,
      dpRoomString: roomString,
      dpTimeStamp: parseDateIntoHermesString(new Date()),
      dpFilterList: gaFilterListShortName(srpConfig),
    };

    let data = {};

    let userSearchEventData = {};
    let apiLoadTime;

    switch (pageName) {
      case 'srp': {
        if (isSeoSrp) {
          const {seoData} = srp;
          const {templateName = ''} = seoData;
          commonDataForMainFunnel = {}; // SEO-SRP has separate set of data. So, main funnel data has to be cleared
          let firstFilterLocation =
            Array.isArray(srpFilter?.location_search) &&
            srpFilter?.location_search.length &&
            srpFilter?.location_search[0];
          data = {
            virtualPage: window && window.location && window.location.pathname,
            cdCityVoy: cityId,
            cdLocName: destinationType === 'city' ? firstFilterLocation?.n || '' : destination.n,
            cdLocVoy:
              destinationType === 'city' ? firstFilterLocation?.loc_id || '' : destination._id,
            cdDaysToBooking: dateDelta,
            cdHotelSearchTitle: destinationType,
            cdHotelSearchCheckIn: checkinDateInHermesString,
            cdHotelSearchCheckOut: checkoutDateInHermesString,
            cdCollectionId: srpFilter?.hctx || '',
            cdCollectionName: srpFilter?.hctxTitle || '',
            cdHotelSearchRooms: roomString,
            event: mwebPageViewGAConfig.mwebPageViewSeoSrpEvent,
            templateName,
          };
        } else {
          data = {
            screenName: mwebPageViewGAConfig.screenNames[pageName],
          };
        }
        userSearchEventData = {
          event: mwebPageViewGAConfig.mwebUserSearchEvent,
          searchCityName: cityName,
          searchCityVoy: cityId,
          searchHotelVoy: '-1',
          searchHotellName: '-1',
          searchType: destinationType,
          searchLocName: destinationType === 'city' ? '-1' : destination.n,
          searchLocVoy: destinationType === 'city' ? '-1' : destination._id,
        };
        break;
      }

      case 'details': {
        data = {
          screenName: mwebPageViewGAConfig.screenNames[pageName],
        };
        userSearchEventData = {
          event: mwebPageViewGAConfig.mwebUserSearchEvent,
          searchCityName: staticData?.c,
          searchCityVoy: staticData?.vcid,
          searchHotelVoy: staticData?.hc || selectedHotel.propertyId || '-1',
          searchHotellName: staticData?.hn || selectedHotel.propertyName || '-1',
          searchType: 'Hotel',
          searchLocName: staticData?.l || '-1',
          searchLocVoy: '999999',
        };
        break;
      }
      case 'calendar':
      case 'progressiveSearchForm':
      case 'locationFilters':
      case 'filters': {
        data = {
          screenName: mwebPageViewGAConfig.screenNames[pageName],
        };
        break;
      }
      case 'review': {
        const {descblockResponse = {}} = review;
        const {cdata = {}} = descblockResponse;
        data = {
          screenName: mwebPageViewGAConfig.screenNames[pageName],
          cdPromoCode: descblockResponse?.promocode || '',
          propertyType: cdata?.ht || '',
          cdPayMode: cdata?.pay_mode || '',
        };
        apiLoadTime = cb();
        if (apiLoadTime) data = {...data, apiLoadTime};
        break;
      }
      default:
        break;
    }
    if (Object.keys(userSearchEventData).length) pushga(userSearchEventData);
    pushga({...commonDataAcrossAllFunnels, ...commonDataForMainFunnel, ...data, ...additionalData});
  };
};

export const gaEcomThunk = (eventName, data = {}): ThunkAction<void, AppState, {}, AnyAction> => {
  return (_: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {ecommerceEvents} = gaConfig;
    const {
      srpImpressionLanding,
      srpProductClick,
      detailsPageLanding,
      addToCart,
      ecomCheckout,
    } = ecommerceEvents;
    const {srp, common, details, review, travellerDetails} = getState();
    const {country} = common;
    const {srpData, srpConfig, isSEO} = srp;
    const {filter = {}, s: sortFn = ''} = srpConfig;
    let ecommerce = {};
    let additionalData = {};
    switch (eventName) {
      case srpImpressionLanding:
      case srpProductClick: {
        const {data: hotelListFromState = [], city_meta_info: cityMetaInfo} = srpData;
        const {coc} = cityMetaInfo;
        const countryCheckVariable = coc || country;

        const category =
          (countryCheckVariable === appConfig.countryTextMap.india.key &&
            appConfig.countryTextMap.india.category) ||
          (countryCheckVariable && appConfig.countryTextMap.international.category) ||
          '';

        const list = gaFilterListShortName({filter, s: sortFn, isSEO});
        if (eventName === srpImpressionLanding) {
          const impressions = [];
          hotelListFromState.forEach(
            (
              {spr: price = '', hn: name = '', hc: id = '', ch: brand = '', hr: variant = ''},
              index,
            ) => {
              impressions.push({
                price,
                name,
                id,
                brand,
                variant,
                position: index + 1,
                list,
                category,
              });
            },
          );
          ecommerce = {...ecommerce, impressions};
        } else {
          const {productDetails = {}, productPosition} = data;
          let click = {
            actionField: {
              action: 'click',
              list,
            },
            products: [],
          };
          if (Object.keys(productDetails).length) {
            const {
              spr: price = '',
              hn: name = '',
              hc: id = '',
              ch: brand = '',
              hr: variant = '',
            } = productDetails;
            click.products.push({
              price,
              name,
              id,
              brand,
              variant,
              position: productPosition,
              category,
            });
          } else return;
          ecommerce = {...ecommerce, click};
        }
        break;
      }
      case detailsPageLanding: {
        const {staticData, hermesData = {}, vhid} = details;
        const {cn: countryName, ch: detailsBrand, hn: hotelName, hr: variant = ''} = staticData;
        const {reg: roomList = []} = hermesData?.data || {};
        if (!roomList || !roomList.length) {
          // sold out
          return;
        }
        //   const isDetailsSeo = !!(window && typeof window.__initial_state__?.details?.staticData == 'object' && Object.keys(window.__initial_state__?.details?.staticData).length)
        const roomOneRpl = roomList[0].rpl && roomList[0].rpl.length ? roomList[0].rpl[0] : {};
        const {pd = {}} = roomOneRpl;
        const {spr: hotelPrice = 0} = pd;
        const detailsListName = gaFilterListShortName({});

        const detailsCategory =
          countryName === appConfig.countryTextMap.india.value
            ? appConfig.countryTextMap.india.category
            : appConfig.countryTextMap.international.category;

        let detail = {
          actionField: {
            list: detailsListName,
            action: 'detail',
          },
          products: [
            {
              brand: detailsBrand,
              category: detailsCategory,
              id: vhid,
              name: hotelName,
              price: hotelPrice,
              variant,
            },
          ],
        };
        ecommerce = {...ecommerce, detail};
        break;
      }

      case addToCart: {
        const {staticData, vhid} = details;
        const {cn: countryName, ch: brand, hn: hotelName, hr: variant = ''} = staticData;
        const category =
          countryName === appConfig.countryTextMap.india.value
            ? appConfig.countryTextMap.india.category
            : appConfig.countryTextMap.international.category;
        const {currency = '', showPrice = ''} = data;
        let add = {
          products: [
            {
              brand: brand,
              category,
              id: vhid,
              name: hotelName,
              price: showPrice,
              variant,
            },
          ],
        };
        ecommerce = {...ecommerce, currencyCode: currency, add};
        break;
      }

      case ecomCheckout: {
        const {descblockResponse = {}, descblockInput} = review;
        const {formData} = travellerDetails;
        if (!descblockResponse?.cdata) return;
        const {
          HotelName = '',
          HotelRating = [],
          is_domestic,
          RoomTypeName = '',
          personalized_keys,
          aftrpromototal = '',
        } = descblockResponse?.cdata;

        const {hc: hotelCode} = descblockInput;

        const payableAmount = (personalized_keys ? personalized_keys?.np_wt : aftrpromototal) || 0;

        let checkout = {
          actionField: {step: 1, option: ''},
          products: [
            {
              name: HotelName,
              id: hotelCode,
              price: payableAmount,
              brand: '',
              category:
                is_domestic === 1
                  ? appConfig.countryTextMap.india.category
                  : appConfig.countryTextMap.international.category,
              variant: HotelRating.length - 1,
              quantity: 1,
            },
          ],
        };
        additionalData = {
          weHotelPrice: payableAmount,
          weHotelStarRating: HotelRating !== undefined ? HotelRating.length - 1 : '',
          weRoomTypeName: RoomTypeName,
          weEmail: formData?.email || '',
        };

        ecommerce = {...ecommerce, checkout};
        break;
      }
      default:
        break;
    }
    const path = (window && window.location && window.location.pathname) || '';

    pushEcommerceGAEvent({ecommerce, event: eventName, path, ...additionalData});
  };
};

export const gaGoogleRTThunk = (pageType = 'home'): ThunkAction<void, AppState, {}, AnyAction> => {
  return (_: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {common, user, srp, details, review} = getState();
    const {destination, checkin, checkout, roomString, partner, country, metaPartner} = common;

    const {userInfo} = user;
    const {srpData} = srp;

    const {adultCount, room} = roomStringUtils.getRoomConfig(roomString);

    const checkinDateInHermesString =
      (checkin && checkin.date && parseDateIntoHermesString(checkin.date)) || '';
    const checkoutDateInHermesString =
      (checkout && checkout.date && parseDateIntoHermesString(checkout.date)) || '';

    let commonData = {
      hrental_id: [],
      hrental_pagetype: pageType,
      hrental_totalvalue: '',
      hrental_startdate: dateFormat(checkin.date, 'yyyy-mm-dd'),
      hrental_enddate: dateFormat(checkout.date, 'yyyy-mm-dd'),
      hrental_locid: '',
      email: userInfo.email ? userInfo.email : '',
      hashedemail: userInfo.hashedemail ? userInfo.hashedemail : '',
      sitetype: 'd',
      adultCount,
      roomCount: room,
      pagetype: pageType,
    };

    let sector = 0;
    switch (pageType) {
      case 'results': {
        const {city_meta_info: cityMetaInfo, data: results} = srpData;
        if (cityMetaInfo.co) {
          sector = cityMetaInfo?.co.toLowerCase() === 'india' ? 0 : 1;
        }
        commonData.hrental_id = results.slice(0, 3).map(item => item.hc);
        commonData.hrental_pagetype = 'searchresults';
        commonData.hrental_locid = destination._id || cityMetaInfo.vcid;
        commonData.email = userInfo.email ? userInfo.email : '';
        commonData.hashedemail = userInfo.hashedemail ? userInfo.hashedemail : '';
        commonData.sector = sector;
        break;
      }
      case 'details': {
        // const {staticData = {}, vhid, hotelsDetails = {}, slotSelected = false} = details;
        const {
          selectedHotelStatic,
          staticData: targetData,
          hermesData: pricingTargetData,
        } = details;
        const {data = {}} = targetData;
        const priceData = pricingTargetData.data ? pricingTargetData.data : {};
        const roomType = 'reg';
        const roomList = priceData[roomType] || [];
        const roomOneRpl =
          roomList[0] && roomList[0].rpl && roomList[0].rpl.length ? roomList[0].rpl[0] : {};
        const {pd = {}} = roomOneRpl;
        const {spr = 0} = pd;
        const {lid = '', hc = '', vcid = '', cn = '', minprice = 0} = targetData;
        if (cn) {
          sector = cn.toLowerCase() === 'india' ? 0 : 1;
        }
        commonData.hrental_id = hc;
        commonData.hrental_totalvalue = spr || minprice;
        commonData.hrental_pagetype = 'product';
        commonData.hrental_locid = lid || destination._id;
        commonData.email = userInfo.email ? userInfo.email : '';
        commonData.hashedemail = userInfo.hashedemail ? userInfo.hashedemail : '';
        commonData.hrental_vcid = vcid;
        commonData.sector = sector;
        break;
      }
      case 'cart': {
        const {descblockResponse: descblock, descblockInput} = review;
        const {cdata = {}} = descblock;

        const {is_domestic, personalized_keys, aftrpromototal} = cdata;
        const {strQry, hc: vhid} = descblockInput;
        const strQuery = strQry ? strQry.split('-') : [];
        const vcid = strQuery[1] || '';

        commonData.hrental_id = vhid;
        commonData.hrental_locid = vcid;
        commonData.hrental_totalvalue =
          (personalized_keys ? personalized_keys.np_wt : aftrpromototal) || 0;
        commonData.sector = is_domestic ? 0 : 1;
        break;
      }
      default:
        break;
    }

    pushga({
      event: 'googleRT',
      google_tag_params: {...commonData},
    });
  };
};
